/**
 * Support at Home suggestion engine — detailed projections modal with breakdown tooltips.
 */
import React, { useState } from 'react';
import { X, LineChart } from 'lucide-react';
import { formatCurrency, calculateAge } from '../../utils/index.ts';
import { CalculationTooltip } from '../../components/ui/index.tsx';
import ProjectionsModal from '../agedCare/ProjectionsModal.tsx';
import { getAnnualMinimumAbpDraw } from '../../../../shared/src/calculation/abpProjectionUtils.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFindAsset } from '../../../../shared/src/calculation/factFindTypes.ts';

type DraftAsset = FactFindAsset;
type DraftAssetMap = Record<string, DraftAsset[] | undefined>;

/** A single labelled tooltip/breakdown row, as produced by the calc engine's various `*Breakdown` bags. */
interface BreakdownRow {
    label: string;
    value: string | number;
}

/** One year of a suggestion-engine strategy projection — a free-form calc-engine result row (see
 * strategy/supportAtHomeProjectionUtils.ts's own `SupportAtHomeProjectionYear`), extended here with
 * the specific fields this deep-dive modal reads for its tooltips. */
interface DeepDiveProjectionYear {
    year?: number | string;
    fyLabel?: string;
    openingNetWorth?: number;
    netWorth?: number;
    netWorthBreakdown?: BreakdownRow[];
    socialSecurity?: number;
    socialSecurityBreakdown?: BreakdownRow[];
    cashFlow?: number;
    cashflow?: number;
    cashFlowBreakdown?: BreakdownRow[];
    /** Human-readable HTML breakdown of the year's calculations (see server `ProjectionYearResult.breakdown`). */
    breakdown?: string;
    cashflowRoutingLine?: { label?: string; value?: string | number };
    annualCareCost?: number;
    annualCareCostForPartialPeriod?: number;
    agedCareBreakdown?: { label: string; value: string | number }[];
    investmentIncome?: number;
    assets?: DraftAssetMap;
    financials?: {
        socialSecurityBreakdown?: BreakdownRow[];
        cashFlowBreakdown?: BreakdownRow[];
        [key: string]: unknown;
    };
    [key: string]: unknown;
}

interface AssetAllocationRow {
    assetName: string;
    amount: number;
    liquidatedAmount: number;
    remainingAmount: number;
    status: string;
}

interface DeepDiveStrategy {
    name?: string;
    projections?: DeepDiveProjectionYear[];
    assetAllocation?: AssetAllocationRow[];
    snapshot?: { netWorth?: number; [key: string]: unknown };
    modifiedFactFind?: { assets?: DraftAssetMap };
    [key: string]: unknown;
}

function parseBreakdownValue(value: unknown): number {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
        return Math.abs(parseFloat(value.replace(/[$,]/g, '')) || 0);
    }
    return 0;
}

function buildAgePensionTooltip(year: DeepDiveProjectionYear): { label: string; value: string }[] {
    if (year.socialSecurityBreakdown?.length) {
        return year.socialSecurityBreakdown.map((item) => ({
            label: item.label,
            value: typeof item.value === 'number' ? formatCurrency(item.value) : String(item.value),
        }));
    }
    if (year.financials?.socialSecurityBreakdown?.length) {
        return year.financials.socialSecurityBreakdown.map((item) => ({
            label: item.label,
            value: typeof item.value === 'number' ? formatCurrency(item.value) : String(item.value),
        }));
    }
    return [{ label: 'Age Pension', value: formatCurrency(year.socialSecurity || 0) }];
}

function buildCashflowTooltip(year: DeepDiveProjectionYear): { label: string; value: string }[] {
    const rows: { label: string; value: string }[] = [];
    const breakdown = year.cashFlowBreakdown || year.financials?.cashFlowBreakdown;
    if (Array.isArray(breakdown)) {
        breakdown.forEach((item) => {
            const val = item.value;
            rows.push({
                label: item.label,
                value: typeof val === 'number'
                    ? `${val >= 0 ? '+' : ''}${formatCurrency(val)}`
                    : String(val),
            });
        });
    }

    const sahCost = year.annualCareCost ?? year.annualCareCostForPartialPeriod ?? 0;
    if (sahCost > 0) {
        rows.push({ label: '--- Support at Home ---', value: '' });
        if (year.agedCareBreakdown?.length) {
            year.agedCareBreakdown.forEach((item) => {
                rows.push({
                    label: item.label,
                    value: typeof item.value === 'string' ? item.value : formatCurrency(parseBreakdownValue(item.value)),
                });
            });
        } else {
            rows.push({ label: 'Less: Support at Home costs', value: `-${formatCurrency(sahCost)}` });
        }
    }

    const net = year.cashFlow ?? year.cashflow ?? 0;
    rows.push({ label: '--- Net cashflow ---', value: '' });
    rows.push({ label: 'Net cashflow', value: `${net >= 0 ? '+' : ''}${formatCurrency(net)}` });

    if (year.cashflowRoutingLine) {
        rows.push({ label: '---', value: '' });
        rows.push({
            label: year.cashflowRoutingLine.label || 'Funding',
            value: String(year.cashflowRoutingLine.value ?? ''),
        });
    }

    return rows.length ? rows : [{ label: 'Net cashflow', value: formatCurrency(net) }];
}

function buildClosingNetWorthTooltip(year: DeepDiveProjectionYear): { label: string; value: string }[] {
    if (year.netWorthBreakdown?.length) {
        return year.netWorthBreakdown.map((item) => ({
            label: item.label,
            value: typeof item.value === 'number' ? formatCurrency(item.value) : String(item.value),
        }));
    }
    return [{ label: 'Closing net worth', value: formatCurrency(year.netWorth || 0) }];
}

function buildInvestmentIncomeTooltip(year: DeepDiveProjectionYear, yearIndex: number, strategy: DeepDiveStrategy, factFind: DraftFactFind): { label: string; value: string }[] {
    const openingAssets: DraftAssetMap | undefined = yearIndex === 0
        ? strategy.modifiedFactFind?.assets
        : strategy.projections?.[yearIndex - 1]?.assets;

    let abpIncome = 0;
    (openingAssets?.accountBasedPensions || []).forEach((asset) => {
        const value = parseFloat(String(asset.value)) || 0;
        const ownerAge = asset.ownership === 'partner' && factFind?.partner?.dob
            ? (calculateAge(factFind.partner.dob) ?? 70) + (yearIndex)
            : factFind?.client?.dob
                ? (calculateAge(factFind.client.dob) ?? 70) + (yearIndex)
                : 70;
        const nominated = parseFloat(String(asset.income)) || 0;
        abpIncome += Math.max(nominated, getAnnualMinimumAbpDraw(value, ownerAge));
    });

    let otherIncome = 0;
    (openingAssets?.bankAccounts || []).forEach((asset) => {
        const value = parseFloat(String(asset.value)) || 0;
        const rate = parseFloat(String(asset.incomeRate)) || 2;
        otherIncome += value * (rate / 100);
    });
    (openingAssets?.shares || []).forEach((asset) => {
        const value = parseFloat(String(asset.value)) || 0;
        const rate = parseFloat(String(asset.incomeRate)) || 4;
        otherIncome += value * (rate / 100);
    });

    return [
        { label: 'ABP income (opening balances)', value: `+${formatCurrency(abpIncome)}` },
        { label: 'Other investment income', value: `+${formatCurrency(otherIncome)}` },
        { label: 'Total (projection)', value: formatCurrency(year.investmentIncome || 0) },
    ];
}

export interface SupportAtHomeSuggestionEngineDeepDiveProps {
    strategy: DeepDiveStrategy;
    factFind: DraftFactFind;
    onClose: () => void;
    estimatedServicePlanNote?: string | null;
}

export default function SupportAtHomeSuggestionEngineDeepDive({
    strategy,
    factFind,
    onClose,
    estimatedServicePlanNote,
}: SupportAtHomeSuggestionEngineDeepDiveProps) {
    const projections = (strategy?.projections || []).slice(0, 10);
    // The same shared engine view every module's projections use — charts, the monthly narrative
    // and per-figure tooltips — fed with THIS strategy's own projection rows.
    const [showFullProjections, setShowFullProjections] = useState(false);

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto border border-slate-200/80"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sticky top-0 border-b border-slate-100 px-6 pt-6 pb-4 bg-gradient-to-br from-slate-50/95 via-white to-teal-50/50 z-10">
                    <div className="flex items-center justify-between gap-4">
                        <h2 className="text-2xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-sky-500 to-teal-500">
                            {strategy.name}
                        </h2>
                        <div className="flex items-center gap-2 shrink-0">
                            {projections.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setShowFullProjections(true)}
                                    className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg"
                                >
                                    <LineChart className="w-4 h-4" /> Charts &amp; monthly breakdown
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={onClose}
                                className="rounded-lg p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                                aria-label="Close"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                    </div>
                    {estimatedServicePlanNote && (
                        <p className="text-xs text-slate-700 bg-white border border-slate-200 border-l-4 border-l-amber-400 rounded-lg px-3 py-2 mt-3">
                            <strong>Service costs:</strong> {estimatedServicePlanNote}
                        </p>
                    )}
                </div>

                <div className="p-6 space-y-6">
                    {(strategy.assetAllocation?.length ?? 0) > 0 && (
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Asset allocation</h3>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 text-sm">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Asset</th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Original</th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Liquidated</th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Remaining</th>
                                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {(strategy.assetAllocation ?? []).map((asset, idx) => (
                                            <tr key={`${asset.assetName}-${asset.status}-${idx}`}>
                                                <td className="px-4 py-3">{asset.assetName}</td>
                                                <td className="px-4 py-3 text-right">{formatCurrency(asset.amount)}</td>
                                                <td className="px-4 py-3 text-right text-red-600">{formatCurrency(asset.liquidatedAmount)}</td>
                                                <td className="px-4 py-3 text-right">{formatCurrency(asset.remainingAmount)}</td>
                                                <td className="px-4 py-3 text-center">
                                                    <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100">
                                                        {asset.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {projections.length > 0 && (
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-2">Yearly projections</h3>
                            <p className="text-xs text-gray-500 mb-4">
                                Hover the underlined values for breakdowns. Cashflow and net worth match the projection engine.
                            </p>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 text-sm">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Year</th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Age Pension</th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Investment income</th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">SAH costs</th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cashflow</th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Net Worth</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {projections.map((year, index) => (
                                            <tr key={year.year ?? index} className={index % 2 ? 'bg-gray-50/60' : ''}>
                                                <td className="px-4 py-3 font-medium">
                                                    {year.fyLabel || `Year ${index + 1}`}
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <CalculationTooltip
                                                        title="Age Pension breakdown"
                                                        data={buildAgePensionTooltip(year)}
                                                    >
                                                        <span className="border-b border-dotted border-gray-400 cursor-help">
                                                            {formatCurrency(year.socialSecurity || 0)}
                                                        </span>
                                                    </CalculationTooltip>
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <CalculationTooltip
                                                        title="Investment income"
                                                        data={buildInvestmentIncomeTooltip(year, index, strategy, factFind)}
                                                    >
                                                        <span className="border-b border-dotted border-gray-400 cursor-help">
                                                            {formatCurrency(year.investmentIncome || 0)}
                                                        </span>
                                                    </CalculationTooltip>
                                                </td>
                                                <td className="px-4 py-3 text-right text-red-600">
                                                    {formatCurrency(year.annualCareCost ?? 0)}
                                                </td>
                                                <td className={`px-4 py-3 text-right font-semibold ${(year.cashFlow ?? year.cashflow ?? 0) >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                                                    <CalculationTooltip
                                                        title="Net cashflow breakdown"
                                                        data={buildCashflowTooltip(year)}
                                                    >
                                                        <span className="border-b border-dotted border-gray-400 cursor-help">
                                                            {formatCurrency(year.cashFlow ?? year.cashflow ?? 0)}
                                                        </span>
                                                    </CalculationTooltip>
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <CalculationTooltip
                                                        title="Net Worth (closing)"
                                                        data={buildClosingNetWorthTooltip(year)}
                                                    >
                                                        <span className="border-b border-dotted border-gray-400 cursor-help">
                                                            {formatCurrency(year.netWorth || 0)}
                                                        </span>
                                                    </CalculationTooltip>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Full engine view for this strategy: charts, monthly narrative, per-figure tooltips.
                The strategy's own projection rows and post-strategy fact-find drive it, so what it
                shows is exactly what the ranking measured. Portals to document.body, so it renders
                above this modal. */}
            {showFullProjections && (
                <ProjectionsModal
                    onClose={() => setShowFullProjections(false)}
                    projections={projections as never}
                    factFind={(strategy.modifiedFactFind ?? factFind) as never}
                    initialFinancials={(strategy.financials ?? null) as never}
                    currentFinancials={(strategy.financials ?? null) as never}
                    agedCareCosts={null}
                    costLabel="Support at Home Costs"
                />
            )}
        </div>
    );
}
